<script setup lang="ts">
import { computed } from 'vue'

import type { AlertDraft, AlertMetricCondition, AlertWindowType } from '../models/alert.types'
import type { AlertValidationKey } from '../service/alertValidator'

import MetricConditionRow from './MetricConditionRow.vue'

import { toReportColumnOption } from '@/features/reports'
import { useI18n } from '@/shared/i18n'
import { Input, MultiSelect, Select, SegmentedControl } from '@/shared/ui'

const props = defineProps<{
  draft: AlertDraft
  fieldsLoading: boolean
  error: AlertValidationKey | null
  readOnly: boolean
}>()
const emit = defineEmits<{
  'update:window': [window: AlertWindowType | null]
  'update:customDays': [days: number | null]
  'update:metricFields': [fields: string[]]
  'update:condition': [field: string, patch: Partial<AlertMetricCondition>]
  'update:conditionJoin': [operator: 'AND' | 'OR']
}>()
const { t } = useI18n()
const UPDATE_CUSTOM_DAYS = 'update:customDays' as const

const window = computed<AlertWindowType | null>({
  get: () => props.draft.window,
  set: (value) => emit('update:window', value),
})
const metricFields = computed<string[]>({
  get: () => props.draft.metrics.map((condition) => condition.column.field),
  set: (fields) => emit('update:metricFields', fields),
})
const windowOptions = computed(() => [
  { value: 'daily' as const, label: t('alerts.window.daily'), disabled: props.readOnly },
  { value: 'weekly' as const, label: t('alerts.window.weekly'), disabled: props.readOnly },
  { value: 'custom' as const, label: t('alerts.window.custom'), disabled: props.readOnly },
])
const metricOptions = computed(() =>
  (props.draft.level?.tableMetrics ?? []).map((column) => ({
    ...toReportColumnOption(column),
  })),
)

function customDays(value: string | number | undefined): void {
  if (value === '' || value === undefined) return emit(UPDATE_CUSTOM_DAYS, null)
  const numeric = Number(value)
  emit(UPDATE_CUSTOM_DAYS, Number.isFinite(numeric) ? numeric : null)
}

function windowTitle(value: unknown): string {
  if (value === 'daily') return t('alerts.window.dailyTitle')
  if (value === 'weekly') return t('alerts.window.weeklyTitle')
  return t('alerts.window.customTitle')
}
</script>

<template>
  <div class="conditions-step grid min-w-0 gap-md py-sm">
    <SegmentedControl
      v-model="window"
      class="conditions-step__periods grid w-full grid-cols-3 gap-xs border-0 bg-transparent p-0 max-sm:grid-cols-1"
      :options="windowOptions"
      :label="t('alerts.condition.window')"
      size="lg"
    >
      <template #option="{ option }">
        <strong class="block">{{ windowTitle(option.value) }}</strong>
        <small class="block text-[0.75rem] font-normal">{{ option.label }}</small>
      </template>
    </SegmentedControl>
    <div
      v-if="draft.window === 'custom'"
      class="conditions-step__custom flex items-center gap-sm rounded-md bg-info-soft p-sm text-[0.75rem] text-info max-sm:flex-wrap"
    >
      <span>{{ t('alerts.condition.customDaysPrefix') }}</span>
      <div class="conditions-step__custom-input w-14">
        <Input
          :model-value="draft.customWindowDays ?? ''"
          type="number"
          size="xs"
          placeholder="x"
          :min="2"
          :max="30"
          :label="t('alerts.condition.customDays')"
          hide-label
          :disabled="readOnly"
          :error="error === 'alerts.validation.customDays'"
          :error-message="t('alerts.validation.customDays')"
          @update:model-value="customDays"
        />
      </div>
      <span>{{ t('alerts.condition.customDaysSuffix') }}</span>
    </div>
    <MultiSelect
      v-model="metricFields"
      :options="metricOptions"
      :label="t('alerts.condition.metrics')"
      :placeholder="t('alerts.condition.metricsPlaceholder')"
      searchable
      :state="fieldsLoading ? 'loading' : 'ready'"
      :disabled="readOnly || !draft.level"
    />
    <p
      v-if="
        error &&
        error !== 'alerts.validation.amount' &&
        error !== 'alerts.validation.customDays' &&
        error !== 'alerts.validation.compareWindow'
      "
      class="conditions-step__error m-0 text-[0.75rem] text-danger"
    >
      {{ t(error) }}
    </p>
    <section
      v-if="draft.metrics.length"
      class="conditions-step__rows grid min-w-0 gap-md overflow-x-auto"
    >
      <h3 class="m-0">{{ t('alerts.condition.triggerWhen') }}</h3>
      <div v-if="draft.metrics.length > 1" class="flex items-center gap-sm">
        <Select
          :model-value="draft.conditionJoin"
          :options="[
            { value: 'OR', label: t('alerts.condition.operator.or') },
            { value: 'AND', label: t('alerts.condition.operator.and') },
          ]"
          :label="t('alerts.condition.operator.label')"
          size="sm"
          variant="picker"
          :disabled="readOnly"
          @update:model-value="emit('update:conditionJoin', $event as 'AND' | 'OR')"
        />
        <span v-if="draft.conditionJoin === 'AND'" class="text-xs text-muted">
          {{ t('alerts.condition.operator.info') }}
        </span>
      </div>
      <MetricConditionRow
        v-for="condition in draft.metrics"
        :key="condition.column.field"
        :condition="condition"
        :window="draft.window"
        :custom-days="draft.customWindowDays"
        :read-only="readOnly"
        :show-operator="false"
        :error="error === 'alerts.validation.amount' && condition.amount === null"
        :compare-error="error === 'alerts.validation.compareWindow'"
        @update="emit('update:condition', condition.column.field, $event)"
      />
    </section>
  </div>
</template>

<style scoped>
.conditions-step .conditions-step__periods :deep(.ui-segmented-control__option) {
  display: grid;
  min-height: 3.75rem;
  place-content: center;
  gap: var(--ui-space-2xs);
  border: 1px solid var(--ui-color-border);
  background-color: var(--ui-color-surface-sunken);
}
.conditions-step .conditions-step__periods :deep(.ui-segmented-control__option--selected) {
  border-color: var(--ui-color-info);
  background-color: var(--ui-color-info-soft);
  color: var(--ui-color-info);
}
</style>
